import {
  Query,
  VaroAdvanceProductsResult,
  VaroAdvanceProductsResultTypename,
  VaroAdvanceProductsResultTypenames,
} from '@varo/graphql-schema-types';
import { LINKED_ACCOUNT_TIMEOUT_MILLISECONDS } from 'features/advance/constants/constants';
import {
  isVaroAdvanceProductsError,
  isVaroAdvanceProductsNotQualified,
  isVaroAdvanceProductsPending,
  isVaroAdvanceProductsQualified,
} from 'features/advance/helpers/Advance.helpers';
import { RefObject, useEffect, useRef, useState } from 'react';
import fetchVaroAdvanceProductV2 from 'services/graphql/queries/advance/fetchVaroAdvanceProductV2';
import { useQuery } from 'urql';

export const getStatusEnum = (
  status: VaroAdvanceProductsResultTypename | undefined,
): VaroAdvanceProductsResultTypenames | undefined => {
  if (!status) {
    return undefined;
  }
  switch (status) {
    case 'VaroAdvanceProductsQualified':
      return VaroAdvanceProductsResultTypenames.VaroAdvanceProductsQualified;
    case 'VaroAdvanceProductsNotQualified':
      return VaroAdvanceProductsResultTypenames.VaroAdvanceProductsNotQualified;
    case 'VaroAdvanceProductsPendingEligibility':
      return VaroAdvanceProductsResultTypenames.VaroAdvanceProductsPendingEligibility;
    default:
      return undefined;
  }
};

interface useAdvanceProductsV2Props {
  pauseQuery?: boolean; // should be in ms NOT seconds
  shouldPoll?: boolean;
  pollInterval?: number;
  pollingCallback?: () => void;
}

const useAdvanceProductsV2 = ({
  pauseQuery = true,
  shouldPoll = true,
  pollInterval = 1000,
  pollingCallback,
}: useAdvanceProductsV2Props) => {
  const [counter, setCounter] = useState<number | null>(null);

  const timer: RefObject<ReturnType<typeof setInterval> | undefined> =
    useRef(undefined);

  const [response, refetch] = useQuery<Query, VaroAdvanceProductsResult>({
    query: fetchVaroAdvanceProductV2,
    requestPolicy: 'network-only',
    pause: pauseQuery,
  });

  const { data, error, fetching } = response;

  useEffect(() => {
    if (shouldPoll) {
      const pollingTimeoutInSeconds =
        LINKED_ACCOUNT_TIMEOUT_MILLISECONDS / 1000;
      // make the initial query and then poll every following 3 seconds
      // if counter is null, we haven't made the initial query yet
      if (counter === null) {
        refetch();
        setCounter(pollingTimeoutInSeconds);
      }
      // if counter is equal to pollingTimeoutInSeconds, we have made the initial query
      // and now we want to set the interval to poll every pollInterval
      if (counter === pollingTimeoutInSeconds) {
        timer.current = setInterval(() => {
          setCounter((secondsLeft) => {
            if (secondsLeft === null) {
              return pollingTimeoutInSeconds;
            }

            const pollIntervalInSeconds = pollInterval / 1000;
            return secondsLeft - pollIntervalInSeconds;
          });

          pollingCallback?.();
          refetch();
        }, pollInterval);
      }
    }
    if (!shouldPoll && timer.current) {
      clearInterval(timer.current);
    }
  }, [counter, pollInterval, pollingCallback, refetch, shouldPoll]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = undefined;
      }
    };
  }, []);

  const { varoAdvanceProducts } = data || {};

  const eligibilityStatus = getStatusEnum(varoAdvanceProducts?.__typename);

  const advanceProductsQualified = isVaroAdvanceProductsQualified(
    varoAdvanceProducts,
  )
    ? varoAdvanceProducts
    : null;

  const advanceProductsNotQualified = isVaroAdvanceProductsNotQualified(
    varoAdvanceProducts,
  )
    ? varoAdvanceProducts
    : null;

  const advanceProductsPending = isVaroAdvanceProductsPending(
    varoAdvanceProducts,
  )
    ? varoAdvanceProducts
    : null;

  const advanceProductsError = isVaroAdvanceProductsError(varoAdvanceProducts)
    ? varoAdvanceProducts
    : null;

  return {
    counter,
    data: {
      advanceProductsQualified,
      advanceProductsNotQualified,
      advanceProductsPending,
      advanceProductsError,
    },
    eligibilityStatus,
    error,
    fetching,
    refetch,
  };
};

export default useAdvanceProductsV2;
